import { PREVIEW_COLORS, PREVIEW_SPACING, PREVIEW_RADIUS } from './constants';
import { ClockIcon } from './PreviewIcons';
import type { SparkActivityPoint } from '../../types/spark-activity';

interface ActivityPreviewContentProps {
  title: string;
  prep: string;
  duration: number;
  durationMax: number | null;
  description: string;
  steps: SparkActivityPoint[];
  whyItWorks: string;
  expertTips: string;
}

function formatDuration(duration: number, durationMax: number | null): string {
  if (durationMax) return `${duration}–${durationMax} min`;
  return `${duration} min`;
}

export default function ActivityPreviewContent({
  title,
  prep,
  duration,
  durationMax,
  description,
  steps,
  whyItWorks,
  expertTips,
}: ActivityPreviewContentProps) {
  const px = PREVIEW_SPACING.pagePadding;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", paddingBottom: `${PREVIEW_SPACING.footerHeight + 8}px` }}>
      {/* Image placeholder */}
      <div
        style={{
          margin: `0 ${px}px ${PREVIEW_SPACING.sectionGap}px`,
          height: `${PREVIEW_SPACING.imageHeight}px`,
          borderRadius: `${PREVIEW_RADIUS.image}px`,
          backgroundColor: '#E8EFF2',
        }}
      />

      <div style={{ padding: `0 ${px}px`, display: 'flex', flexDirection: 'column', gap: `${PREVIEW_SPACING.sectionGap}px` }}>
        {/* Title */}
        <h1
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: PREVIEW_COLORS.textPrimary,
            letterSpacing: '-0.04em',
            lineHeight: 1,
            margin: 0,
          }}
        >
          {title || 'Activity Title'}
        </h1>

        {/* Metadata badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: PREVIEW_COLORS.textSecondary, fontSize: '14px' }}>
            <ClockIcon />
            <span>{formatDuration(duration, durationMax)}</span>
          </div>
          {prep && (
            <>
              <span style={{ color: PREVIEW_COLORS.tagDivider, fontSize: '14px' }}>·</span>
              <span style={{ color: PREVIEW_COLORS.textSecondary, fontSize: '14px' }}>{prep}</span>
            </>
          )}
        </div>

        {/* Description */}
        {description && (
          <p style={{ fontSize: '16px', color: PREVIEW_COLORS.textBody, lineHeight: '1.75', margin: 0 }}>
            {description}
          </p>
        )}

        {/* Steps */}
        {steps.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: `${PREVIEW_SPACING.elementGap}px` }}>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 600,
                color: PREVIEW_COLORS.primaryBlue,
                letterSpacing: '-0.04em',
                margin: 0,
              }}
            >
              Try this
            </h2>
            {steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: `${PREVIEW_SPACING.stepNumberSize}px`,
                    height: `${PREVIEW_SPACING.stepNumberSize}px`,
                    borderRadius: `${PREVIEW_RADIUS.stepBadge}px`,
                    backgroundColor: PREVIEW_COLORS.stepNumberBg,
                    color: PREVIEW_COLORS.stepNumberText,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </div>
                <p style={{ fontSize: '16px', color: PREVIEW_COLORS.textBody, margin: 0, lineHeight: '1.5', paddingTop: '3px' }}>
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Expert Tips */}
        {expertTips && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: `${PREVIEW_SPACING.elementGap}px` }}>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 600,
                color: PREVIEW_COLORS.primaryBlue,
                letterSpacing: '-0.04em',
                margin: 0,
              }}
            >
              How to make play last longer
            </h2>
            <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {expertTips.split('\n').filter(Boolean).map((tip, i) => (
                <li key={i} style={{ fontSize: '16px', color: PREVIEW_COLORS.textBody, lineHeight: '1.5' }}>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Why It Works */}
        {whyItWorks && (
          <div
            style={{
              backgroundColor: PREVIEW_COLORS.infoBoxBg,
              borderRadius: `${PREVIEW_RADIUS.infoBox}px`,
              padding: `${PREVIEW_SPACING.cardPadding}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <h3
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: PREVIEW_COLORS.infoBoxText,
                margin: 0,
                letterSpacing: '-0.02em',
              }}
            >
              Why this works
            </h3>
            <p style={{ fontSize: '14px', color: PREVIEW_COLORS.textBody, margin: 0, lineHeight: '1.5' }}>
              {whyItWorks}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
